import type { DateField, DateOp, FilterError, Pattern, Person, Term } from "./ast";
import { parseDateValue } from "./dates";
import type { Char, Token } from "./tokenizer";

type TextToken = Extract<Token, { type: "text" }>;

export class TermError extends Error {
  constructor(readonly error: FilterError) {
    super(error.message);
  }
}

/** Lowercases and collapses whitespace; Chinese keywords may be written with or without spaces. */
const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
const squash = (s: string) => s.toLowerCase().replace(/\s+/g, "");

/** Keywords without a value. Keys are normalized (lowercase, single spaces). */
const KEYWORDS: Record<string, Term> = {};
function keyword(term: Term, ...names: string[]) {
  for (const n of names) KEYWORDS[norm(n)] = term;
}
keyword({ kind: "overdue" }, "overdue", "over due", "od", "过期", "已过期", "逾期", "到期");
keyword({ kind: "no_date" }, "no date", "no due date", "无日期", "没有日期", "无到期日", "没有到期日");
keyword({ kind: "no_deadline" }, "no deadline", "没有截止期限", "无截止期限", "没有截止日期", "无截止日期");
keyword({ kind: "no_time" }, "no time", "没有时间", "无时间");
keyword({ kind: "recurring" }, "recurring", "循环", "重复");
keyword({ kind: "no_priority" }, "no priority", "没有优先级", "无优先级");
keyword({ kind: "no_labels" }, "no labels", "no label", "没有标签", "无标签");
keyword({ kind: "subtask" }, "subtask", "subtasks", "sub-task", "sub-tasks", "子任务");
keyword({ kind: "assigned" }, "assigned", "已分配", "已指派");
keyword({ kind: "shared" }, "shared", "共享", "已共享");
keyword({ kind: "uncompletable" }, "uncompletable", "不能完成", "不可完成");
keyword({ kind: "all" }, "view all", "all", "查看全部", "全部");
for (const level of [1, 2, 3, 4] as const) {
  keyword({ kind: "priority", level }, `p${level}`, `priority ${level}`, `优先级${level}`, `优先级 ${level}`);
}

type KeyedBuilder = (
  value: string,
  valueChars: Char[],
  err: (msg: string, code?: FilterError["code"]) => never,
) => Term;

const dateKey =
  (field: DateField, op: DateOp): KeyedBuilder =>
  (value, _chars, err) => {
    const v = parseDateValue(value);
    if (!v) return err(`Unrecognized date: "${value}"`, "INVALID_DATE");
    return { kind: "date", field, op, value: v };
  };

function person(value: string, chars: Char[]): Person {
  const n = norm(value);
  if (["me", "myself", "我", "我自己"].includes(n)) return { kind: "me" };
  if (["others", "other", "其他人", "他人", "别人", "其他"].includes(n)) return { kind: "others" };
  return { kind: "name", pattern: toPattern(chars) };
}

/** `key: value` terms. Keys are compared with all whitespace removed. */
const KEYED: Record<string, KeyedBuilder> = {};
function keyed(builder: KeyedBuilder, ...names: string[]) {
  for (const n of names) KEYED[squash(n)] = builder;
}
keyed(dateKey("due", "on"), "date", "日期");
keyed(dateKey("due", "before"), "date before", "在此日期前", "日期前", "日期之前");
keyed(dateKey("due", "after"), "date after", "在此日期后", "日期后", "日期之后");
keyed(dateKey("due_or_deadline", "on"), "due", "到期日");
keyed(dateKey("due_or_deadline", "before"), "due before", "到期前", "到期日前");
keyed(dateKey("due_or_deadline", "after"), "due after", "到期后", "到期日后");
keyed(dateKey("deadline", "on"), "deadline", "截止期限", "截止日期");
keyed(dateKey("deadline", "before"), "deadline before", "截止期限前", "截止日期前", "截止期限之前");
keyed(dateKey("deadline", "after"), "deadline after", "截止期限后", "截止日期后", "截止期限之后");
keyed(dateKey("created", "on"), "created", "创建", "创建于");
keyed(dateKey("created", "before"), "created before", "在此前创建", "创建前", "创建于之前");
keyed(dateKey("created", "after"), "created after", "在此后创建", "创建后", "创建于之后");
keyed((v, c) => ({ kind: "assigned_to", person: person(v, c) }), "assigned to", "分配给", "指派给");
keyed((v, c) => ({ kind: "assigned_by", person: person(v, c) }), "assigned by", "分配自", "分配者", "指派自");
keyed((v, c) => ({ kind: "added_by", person: person(v, c) }), "added by", "添加者", "添加自", "创建者");
keyed((v) => ({ kind: "search", text: v, implicit: false }), "search", "搜索", "搜寻", "查找");
keyed((_v, c) => ({ kind: "workspace", pattern: toPattern(c) }), "workspace", "工作空间", "团队");

/** Splits on unescaped `*`. */
export function toPattern(chars: Char[]): Pattern {
  const parts: string[] = [""];
  for (const ch of chars) {
    if (ch.c === "*" && !ch.esc) parts.push("");
    else parts[parts.length - 1] += ch.c;
  }
  const text = chars.map((ch) => ch.c).join("");
  return { text, parts, wildcard: parts.length > 1 };
}

function trimChars(chars: Char[]): Char[] {
  let a = 0;
  let b = chars.length;
  while (a < b && !chars[a]?.esc && /\s/.test(chars[a]?.c ?? "")) a++;
  while (b > a && !chars[b - 1]?.esc && /\s/.test(chars[b - 1]?.c ?? "")) b--;
  return chars.slice(a, b);
}

/** Turns one text token into a term (keywords, `key: value`, sigils, dates, else plain-text search). */
export function parseTerm(tok: TextToken, lang?: string): Term {
  const chars = tok.chars;
  const text = chars.map((c) => c.c).join("");
  const fail = (msg: string, code: FilterError["code"], start = tok.start, end = tok.end): never => {
    throw new TermError({ code, message: msg, start, end });
  };

  // Sigils: ##project, #project, /section, @label (also %label, as the current Help Center writes it).
  const first = chars[0];
  if (first && !first.esc && ["#", "/", "@", "%"].includes(first.c)) {
    const sub = first.c === "#" && chars[1]?.c === "#" && !chars[1].esc;
    const rest = trimChars(chars.slice(sub ? 2 : 1));
    const kindName = first.c === "#" ? "project" : first.c === "/" ? "section" : "label";
    if (rest.length === 0) fail(`Missing ${kindName} name after "${sub ? "##" : first.c}"`, "MISSING_VALUE");
    const pattern = toPattern(rest);
    if (first.c === "#") return { kind: "project", pattern, withSubprojects: sub };
    if (first.c === "/") return { kind: "section", pattern };
    return { kind: "label", pattern };
  }

  const kw = KEYWORDS[norm(text)];
  if (kw) return kw;

  // `key: value` — the first unescaped colon.
  const colon = chars.findIndex((c) => c.c === ":" && !c.esc);
  if (colon > 0) {
    const key = squash(
      chars
        .slice(0, colon)
        .map((c) => c.c)
        .join(""),
    );
    const builder = KEYED[key];
    if (builder) {
      const valueChars = trimChars(chars.slice(colon + 1));
      const value = valueChars.map((c) => c.c).join("");
      const ci = tok.source.search(/[:：]/);
      const after = tok.source.slice(ci + 1);
      const valueStart = tok.start + ci + 1 + (after.length - after.trimStart().length);
      if (!value) fail(`Missing value after "${tok.source.slice(0, ci + 1)}"`, "MISSING_VALUE", tok.end, tok.end);
      return builder(value, valueChars, (msg, code = "INVALID_DATE") => fail(msg, code, valueStart, tok.end));
    }
  }

  const date = parseDateValue(text, lang, true);
  if (date) return { kind: "date", field: "due_or_deadline", op: "on", value: date };
  return { kind: "search", text, implicit: true };
}
